import { Injectable, inject } from '@angular/core';
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Dialog } from '@angular/cdk/dialog';
import { TranslateService } from '@ngx-translate/core';

import { WsService } from '@app/services/ws.service';
import { ServerResponse } from '@app/enums/server-response';
import { ServerMessageType } from '@app/enums/server-message-type';
import { ErrorCode } from '@app/enums/errors';
import { PollDTO, toPoll } from '@app/models/poll';
import { ServerMsgPlayerVoteResponse } from '@app/models/server-msg';
import { GenericDialogComponent } from '@app/components/generic-dialog/generic-dialog.component';
import { PollsService } from '@app/features/main/services/polls.service';

import * as PollsActions from '@app/store/features/polls/polls.actions';
import * as ConfigSelectors from '@app/store/features/config/config.selectors';

@Injectable()
export class PollsEffects {
    private readonly _ws = inject(WsService);
    private readonly _actions$ = inject(Actions);
    private readonly _store = inject(Store);
    private readonly _dialog = inject(Dialog);
    private readonly _translate = inject(TranslateService);
    private readonly _pollsService = inject(PollsService);

    // ask the server for the polls the player can participate in, once the app is ready (also after a reconnect)
    getPlayersPolls$ = createEffect(
        () =>
            this._store.pipe(
                select(ConfigSelectors.getAppReadyToUse),
                distinctUntilChanged(),
                filter(appReadyToUse => !!appReadyToUse),
                tap(() => this._pollsService.getPlayersPolls())
            ),
        { dispatch: false }
    );

    // CustomMessageType.PlayerPolls = 58, sent as a reply to GetPlayersPolls and whenever a poll is updated in the Admin Panel
    playerPolls$ = createEffect(() =>
        this._ws.getDataResponse<PollDTO[] | null>(ServerResponse.PlayerPolls)
            .pipe(
                map(polls => (polls ?? []).map(toPoll)),
                map(polls => PollsActions.setAll({ polls }))
            ));

    onVote$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(PollsActions.vote),
                switchMap(({ pollId, option }) => {
                    this._pollsService.vote(pollId, option);

                    return this._ws.getServerMsg<ServerMsgPlayerVoteResponse>(ServerMessageType.PlayerVoteResponse)
                        .pipe(
                            take(1),
                            map(response => ({ pollId, response }))
                        );
                }),
                tap(({ response }) => {
                    const dialog = this._dialog.open(GenericDialogComponent, { width: '300px' })
                    dialog.componentInstance!.title = this._translate.instant('main.dialog.polls.title');
                    dialog.componentInstance!.confirmBtn = this._translate.instant('main.dialog.polls.buttons.ok');

                    // server may omit Value when there is no error
                    const errorCode = response.value ?? ErrorCode.NoError;

                    switch (errorCode) {
                        case ErrorCode.NoError:
                            dialog.componentInstance!.text = this._translate.instant('main.dialog.polls.messages.success');
                            break;
                        case ErrorCode.PlayerVoteAlreadyVoted:
                            dialog.componentInstance!.text = this._translate.instant('main.dialog.polls.messages.alreadyVoted');
                            break;
                        case ErrorCode.PlayerVoteUnknownPollOrExpired:
                            dialog.componentInstance!.text = this._translate.instant('main.dialog.polls.messages.unknownOrExpired');
                            break;
                        case ErrorCode.PlayerVoteRestrictedPoll:
                            dialog.componentInstance!.text = this._translate.instant('main.dialog.polls.messages.restricted');
                            break;
                        default:
                            dialog.componentInstance!.text = this._translate.instant('main.dialog.polls.messages.failed');
                            break;
                    }
                }),
                // the poll is no longer pending for this player: voted, already voted, or gone/expired
                filter(({ response }) => {
                    const errorCode = response.value ?? ErrorCode.NoError;
                    return errorCode === ErrorCode.NoError
                        || errorCode === ErrorCode.PlayerVoteAlreadyVoted
                        || errorCode === ErrorCode.PlayerVoteUnknownPollOrExpired;
                }),
                map(({ pollId }) => PollsActions.removeOne({ pollId }))
            )
    );
}
